import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleMarker, useMap } from 'react-leaflet';
import L, { LatLngBounds } from 'leaflet';

/**
 * Reusable canvas-rendered stop layer (PRD-04 FR3): `L.circleMarker` on a shared
 * `L.canvas` (never DOM markers), viewport-culled + capped, **no per-stop Popup in
 * the React tree** (click → callback drives a single shared detail panel). The caller
 * supplies the stop set (important-only by default, all stops for a selected route).
 */
export interface CanvasStop {
  stopId: string;
  lat: number;
  lng: number;
  name?: string | null;
  important?: boolean;
}

interface Props {
  stops: CanvasStop[];
  selectedStopId?: string | null;
  cap?: number;
  onStopClick?: (stopId: string) => void;
  /** When false (network display), `stops` are already viewport-scoped server-side —
   * skip client bounds culling to avoid zoom flicker. */
  viewportCull?: boolean;
  /** Shared canvas renderer — see `CanvasLineLayer`. Pass the same renderer as the
   * co-located line layers so all vectors draw onto one canvas and clicks aren't swallowed
   * by a stacked pane. Falls back to a private renderer when omitted. */
  renderer?: L.Canvas;
}

const DEFAULT_CAP = 2000;

const CanvasStopLayer: React.FC<Props> = ({ stops, selectedStopId, cap = DEFAULT_CAP, onStopClick, viewportCull = true, renderer }) => {
  const map = useMap();
  const internalRenderer = useRef(L.canvas({ padding: 0.5 }));
  const activeRenderer = renderer ?? internalRenderer.current;
  const [bounds, setBounds] = useState<LatLngBounds>(() => map.getBounds());

  useEffect(() => {
    if (!viewportCull) return undefined;
    const onChange = () => setBounds(map.getBounds());
    map.on('moveend', onChange);
    map.on('zoomend', onChange);
    return () => {
      map.off('moveend', onChange);
      map.off('zoomend', onChange);
    };
  }, [map, viewportCull]);

  const onClickRef = useRef(onStopClick);
  useEffect(() => {
    onClickRef.current = onStopClick;
  }, [onStopClick]);

  const elements = useMemo(() => {
    const padded = viewportCull ? bounds.pad(0.2) : null;
    const out: React.ReactElement[] = [];
    let rendered = 0;

    for (const s of stops) {
      if (typeof s.lat !== 'number' || typeof s.lng !== 'number' || !isFinite(s.lat) || !isFinite(s.lng)) {
        continue;
      }
      const selected = s.stopId === selectedStopId;
      if (!selected) {
        if (padded && !padded.contains(L.latLng(s.lat, s.lng))) continue;
        if (rendered >= cap) continue;
      }
      rendered += 1;
      out.push(
        <CircleMarker
          key={s.stopId}
          center={[s.lat, s.lng]}
          radius={selected ? 6 : 4}
          pathOptions={{
            color: selected ? '#EE7334' : '#1565c0',
            weight: selected ? 2 : 1,
            fillColor: selected ? '#EE7334' : '#42a5f5',
            fillOpacity: 0.9,
            renderer: activeRenderer,
          }}
          eventHandlers={{ click: () => onClickRef.current?.(s.stopId) }}
        />,
      );
    }
    return out;
  }, [stops, selectedStopId, bounds, cap, viewportCull, activeRenderer]);

  return <>{elements}</>;
};

export default CanvasStopLayer;
